"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo, useState, useDeferredValue } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { api } from "@/lib/trpc/client";
interface HoldersSectionProps {
  slug: string;
  symbol: string;
}
function formatBalance(value: number, symbol: string): string {
  if (value >= 1e9) return `${(value / 1e9).toFixed(2)}B ${symbol}`;
  if (value >= 1e6) return `${(value / 1e6).toFixed(2)}M ${symbol}`;
  if (value >= 1e3) return `${(value / 1e3).toFixed(2)}K ${symbol}`;
  return `${value.toFixed(2)} ${symbol}`;
}
function formatDate(d: Date | string): string {
  return new Date(d).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric"
  });
}
function shortAddress(addr: string): string {
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

// Quick-select whale thresholds (in token units)
const THRESHOLD_PRESETS = [0, 100, 1_000, 10_000, 100_000, 1_000_000];
export function HoldersSection(t0) {
  const $ = _c(127);
  const {
    slug,
    symbol
  } = t0;
  const [threshold, setThreshold] = useState(10000);
  const deferredThreshold = useDeferredValue(threshold);
  let t1;
  if ($[0] !== deferredThreshold || $[1] !== slug) {
    t1 = {
      slug,
      whaleThreshold: deferredThreshold
    };
    $[0] = deferredThreshold;
    $[1] = slug;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const holdersQuery = api.token.getHolders.useQuery(t1);
  let t2;
  if ($[3] !== deferredThreshold || $[4] !== slug) {
    t2 = {
      slug,
      whaleThreshold: deferredThreshold,
      days: 90
    };
    $[3] = deferredThreshold;
    $[4] = slug;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const historyQuery = api.token.getHolderHistory.useQuery(t2);
  const data = holdersQuery.data;
  let t3;
  if ($[6] !== historyQuery.data) {
    t3 = historyQuery.data ?? [];
    $[6] = historyQuery.data;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const history = t3;
  let t4;
  if ($[8] !== history) {
    t4 = history.map(_temp);
    $[8] = history;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const chartData = t4;
  let t5;
  bb0: {
    if (!data) {
      let t6;
      if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = [];
        $[10] = t6;
      } else {
        t6 = $[10];
      }
      t5 = t6;
      break bb0;
    }
    let t6;
    if ($[11] !== data.topHolders || $[12] !== deferredThreshold) {
      let t7;
      if ($[14] !== deferredThreshold) {
        t7 = h_0 => h_0.balance >= deferredThreshold;
        $[14] = deferredThreshold;
        $[15] = t7;
      } else {
        t7 = $[15];
      }
      t6 = data.topHolders.filter(t7);
      $[11] = data.topHolders;
      $[12] = deferredThreshold;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    t5 = t6;
  }
  const highlightedHolders = t5;
  if (holdersQuery.isLoading) {
    let t6;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <section className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(230,15%,8%)] p-6"><div className="h-96 flex items-center justify-center text-gray-500 text-sm">Loading holder data…</div></section>;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    return t6;
  }
  if (!data) {
    return null;
  }
  let t6;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <h2 className="text-base sm:text-lg font-semibold text-white">On-Chain Holders</h2>;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== data.capturedAt || $[19] !== data.source) {
    t7 = data.capturedAt && <span className="text-xs text-gray-500">Updated {new Date(data.capturedAt).toLocaleDateString()}{data.source === "live" && <span className="ml-2 text-amber-400">· live</span>}</span>;
    $[18] = data.capturedAt;
    $[19] = data.source;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t7) {
    t8 = <div className="flex items-start justify-between flex-wrap gap-3 mb-1">{t6}{t7}</div>;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <p className="text-xs text-gray-400 break-words">Snapshot data from Ethplorer · top 100 holders tracked daily</p>;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t8) {
    t10 = <div>{t8}{t9}</div>;
    $[24] = t8;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <p className="text-xs text-gray-400 mb-1">Total Holders</p>;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== data.totalHolders) {
    t12 = data.totalHolders.toLocaleString();
    $[27] = data.totalHolders;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t12) {
    t13 = <div className="bg-[hsl(230,15%,10%)] rounded-lg p-3 sm:p-4">{t11}<p className="text-base sm:text-lg font-semibold text-white tabular-nums">{t12}</p></div>;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <p className="text-xs text-gray-400 mb-1">Top 10 Share</p>;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const t15 = data.top10Share * 100;
  let t16;
  if ($[32] !== t15) {
    t16 = t15.toFixed(1);
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t16) {
    t17 = <div className="bg-[hsl(230,15%,10%)] rounded-lg p-3 sm:p-4">{t14}<p className="text-base sm:text-lg font-semibold text-purple-400 tabular-nums">{t16}%</p></div>;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <p className="text-xs text-gray-400 mb-1">Top 100 Share</p>;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  const t19 = data.top100Share * 100;
  let t20;
  if ($[37] !== t19) {
    t20 = t19.toFixed(1);
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== t20) {
    t21 = <div className="bg-[hsl(230,15%,10%)] rounded-lg p-3 sm:p-4">{t18}<p className="text-base sm:text-lg font-semibold text-cyan-400 tabular-nums">{t20}%</p></div>;
    $[39] = t20;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== deferredThreshold) {
    t22 = deferredThreshold.toLocaleString();
    $[41] = deferredThreshold;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] !== t22) {
    t23 = <p className="text-xs text-gray-400 mb-1 break-words">Whales (≥ {t22})</p>;
    $[43] = t22;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] !== data.whales.count) {
    t24 = <p className="text-base sm:text-lg font-semibold text-amber-400 tabular-nums">{data.whales.count}</p>;
    $[45] = data.whales.count;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  let t25;
  if ($[47] !== t23 || $[48] !== t24) {
    t25 = <div className="bg-[hsl(230,15%,10%)] rounded-lg p-3 sm:p-4">{t23}{t24}</div>;
    $[47] = t23;
    $[48] = t24;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[50] !== t13 || $[51] !== t17 || $[52] !== t21 || $[53] !== t25) {
    t26 = <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">{t13}{t17}{t21}{t25}</div>;
    $[50] = t13;
    $[51] = t17;
    $[52] = t21;
    $[53] = t25;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  let t27;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <p className="text-sm font-medium text-white">Whale Threshold</p>;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  let t28;
  if ($[56] !== symbol) {
    t28 = <div>{t27}<p className="text-xs text-gray-400 mt-0.5">Count holders with more than this many {symbol}</p></div>;
    $[56] = symbol;
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  let t29;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = e => setThreshold(Math.max(0, Number(e.target.value) || 0));
    $[58] = t29;
  } else {
    t29 = $[58];
  }
  let t30;
  if ($[59] !== threshold) {
    t30 = <input type="number" inputMode="numeric" min={0} step={100} value={threshold} onChange={t29} className="w-28 sm:w-32 min-h-[44px] bg-[hsl(230,15%,6%)] border border-[hsl(230,15%,18%)] rounded px-3 py-2 text-base sm:text-sm text-white focus:outline-none focus:border-blue-500 tabular-nums" />;
    $[59] = threshold;
    $[60] = t30;
  } else {
    t30 = $[60];
  }
  let t31;
  if ($[61] !== symbol) {
    t31 = <span className="text-sm text-gray-400">{symbol}</span>;
    $[61] = symbol;
    $[62] = t31;
  } else {
    t31 = $[62];
  }
  let t32;
  if ($[63] !== t30 || $[64] !== t31) {
    t32 = <div className="flex items-center gap-2">{t30}{t31}</div>;
    $[63] = t30;
    $[64] = t31;
    $[65] = t32;
  } else {
    t32 = $[65];
  }
  let t33;
  if ($[66] !== t28 || $[67] !== t32) {
    t33 = <div className="flex items-center justify-between flex-wrap gap-3 mb-3">{t28}{t32}</div>;
    $[66] = t28;
    $[67] = t32;
    $[68] = t33;
  } else {
    t33 = $[68];
  }
  let t34;
  if ($[69] !== threshold) {
    t34 = THRESHOLD_PRESETS.map(preset => <button key={preset} onClick={() => setThreshold(preset)} className={`inline-flex items-center min-h-[44px] px-3.5 py-2 rounded-full text-xs font-medium transition-colors ${threshold === preset ? "bg-blue-500 text-white" : "bg-[hsl(230,15%,14%)] text-gray-400 hover:bg-[hsl(230,15%,18%)] hover:text-white"}`}>{preset === 0 ? "All" : `≥ ${preset.toLocaleString()}`}</button>);
    $[69] = threshold;
    $[70] = t34;
  } else {
    t34 = $[70];
  }
  let t35;
  if ($[71] !== t34) {
    t35 = <div className="flex flex-wrap gap-2">{t34}</div>;
    $[71] = t34;
    $[72] = t35;
  } else {
    t35 = $[72];
  }
  let t36;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <p className="text-xs text-gray-500">Matching holders</p>;
    $[73] = t36;
  } else {
    t36 = $[73];
  }
  let t37;
  if ($[74] !== data.whales.count) {
    t37 = <div>{t36}<p className="text-white font-semibold tabular-nums">{data.whales.count}</p></div>;
    $[74] = data.whales.count;
    $[75] = t37;
  } else {
    t37 = $[75];
  }
  let t38;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <p className="text-xs text-gray-500">Supply controlled</p>;
    $[76] = t38;
  } else {
    t38 = $[76];
  }
  const t39 = data.whales.totalShare * 100;
  let t40;
  if ($[77] !== t39) {
    t40 = t39.toFixed(2);
    $[77] = t39;
    $[78] = t40;
  } else {
    t40 = $[78];
  }
  let t41;
  if ($[79] !== t40) {
    t41 = <div>{t38}<p className="text-amber-400 font-semibold tabular-nums">{t40}%</p></div>;
    $[79] = t40;
    $[80] = t41;
  } else {
    t41 = $[80];
  }
  let t42;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <p className="text-xs text-gray-500">Combined balance</p>;
    $[81] = t42;
  } else {
    t42 = $[81];
  }
  let t43;
  if ($[82] !== data.whales.totalBalance || $[83] !== symbol) {
    t43 = formatBalance(data.whales.totalBalance, symbol);
    $[82] = data.whales.totalBalance;
    $[83] = symbol;
    $[84] = t43;
  } else {
    t43 = $[84];
  }
  let t44;
  if ($[85] !== t43) {
    t44 = <div>{t42}<p className="text-white font-semibold tabular-nums">{t43}</p></div>;
    $[85] = t43;
    $[86] = t44;
  } else {
    t44 = $[86];
  }
  let t45;
  if ($[87] !== t37 || $[88] !== t41 || $[89] !== t44) {
    t45 = <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">{t37}{t41}{t44}</div>;
    $[87] = t37;
    $[88] = t41;
    $[89] = t44;
    $[90] = t45;
  } else {
    t45 = $[90];
  }
  let t46;
  if ($[91] !== t33 || $[92] !== t35 || $[93] !== t45) {
    t46 = <div className="bg-[hsl(230,15%,10%)] rounded-lg p-3 sm:p-4">{t33}{t35}{t45}</div>;
    $[91] = t33;
    $[92] = t35;
    $[93] = t45;
    $[94] = t46;
  } else {
    t46 = $[94];
  }
  let t47;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <div className="flex items-center justify-between mb-2"><p className="text-sm font-medium text-white">{"Holder Growth & Whale Accumulation"}</p><p className="text-xs text-gray-500">Last 90 days</p></div>;
    $[95] = t47;
  } else {
    t47 = $[95];
  }
  let t48;
  if ($[96] !== chartData || $[97] !== deferredThreshold) {
    t48 = <div>{t47}{chartData.length > 1 ? <div className="h-64 w-full"><ResponsiveContainer width="100%" height="100%"><LineChart data={chartData} margin={{
            top: 5,
            right: 5,
            bottom: 5,
            left: 0
          }}><CartesianGrid stroke="hsl(230, 15%, 14%)" strokeDasharray="3 3" /><XAxis dataKey="timestamp" type="number" domain={["dataMin", "dataMax"]} tickFormatter={_temp2} stroke="#64748b" fontSize={11} minTickGap={40} /><YAxis yAxisId="left" stroke="#64748b" fontSize={11} width={55} tickFormatter={_temp3} /><YAxis yAxisId="right" orientation="right" stroke="#f59e0b" fontSize={11} width={40} /><Tooltip contentStyle={{
              backgroundColor: "hsl(230, 15%, 10%)",
              border: "1px solid hsl(230, 15%, 18%)",
              borderRadius: 8,
              fontSize: 12
            }} labelFormatter={_temp4} /><Legend wrapperStyle={{
              fontSize: 11
            }} /><Line yAxisId="left" type="monotone" dataKey="holders" name="Total holders" stroke="#3b82f6" strokeWidth={2} dot={false} isAnimationActive={false} /><Line yAxisId="right" type="monotone" dataKey="whales" name={`Whales ≥ ${deferredThreshold.toLocaleString()}`} stroke="#f59e0b" strokeWidth={2} dot={false} isAnimationActive={false} /></LineChart></ResponsiveContainer></div> : <div className="h-40 flex items-center justify-center text-xs text-gray-500 border border-dashed border-[hsl(230,15%,14%)] rounded">Historical data starts accumulating once the daily sync runs. Check back tomorrow.</div>}</div>;
    $[96] = chartData;
    $[97] = deferredThreshold;
    $[98] = t48;
  } else {
    t48 = $[98];
  }
  const t49 = highlightedHolders.length > 0 && deferredThreshold > 0 ? `Whale Holders (${highlightedHolders.length})` : "Top Holders";
  let t50;
  if ($[99] !== t49) {
    t50 = <p className="text-sm font-medium text-white">{t49}</p>;
    $[99] = t49;
    $[100] = t50;
  } else {
    t50 = $[100];
  }
  let t51;
  if ($[101] !== data.topHolders || $[102] !== deferredThreshold || $[103] !== symbol) {
    t51 = deferredThreshold > 0 ? `Filtered to ≥ ${deferredThreshold.toLocaleString()} ${symbol}` : `Top ${data.topHolders.length}`;
    $[101] = data.topHolders;
    $[102] = deferredThreshold;
    $[103] = symbol;
    $[104] = t51;
  } else {
    t51 = $[104];
  }
  let t52;
  if ($[105] !== t51) {
    t52 = <p className="text-xs text-gray-500">{t51}</p>;
    $[105] = t51;
    $[106] = t52;
  } else {
    t52 = $[106];
  }
  let t53;
  if ($[107] !== t50 || $[108] !== t52) {
    t53 = <div className="flex items-center justify-between mb-2">{t50}{t52}</div>;
    $[107] = t50;
    $[108] = t52;
    $[109] = t53;
  } else {
    t53 = $[109];
  }
  let t54;
  if ($[110] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = <thead><tr className="text-gray-400 text-xs uppercase tracking-wider"><th className="text-left font-medium py-2 w-8">#</th><th className="text-left font-medium py-2">Address</th><th className="text-right font-medium py-2">Balance</th><th className="text-right font-medium py-2 hidden sm:table-cell">Share</th></tr></thead>;
    $[110] = t54;
  } else {
    t54 = $[110];
  }
  const t55 = deferredThreshold > 0 ? highlightedHolders : data.topHolders;
  let t56;
  if ($[111] !== symbol || $[112] !== t55) {
    let t57;
    if ($[114] !== symbol) {
      t57 = (h_1, i) => <tr key={h_1.address} className="border-t border-[hsl(230,15%,12%)] hover:bg-[hsl(230,15%,10%)] transition-colors"><td className="py-2 text-gray-500 text-xs">{i + 1}</td><td className="py-2"><a href={`https://etherscan.io/address/${h_1.address}`} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-[44px] items-center font-mono text-blue-400 hover:text-blue-300">{shortAddress(h_1.address)}</a></td><td className="py-2 text-right text-white tabular-nums whitespace-nowrap">{formatBalance(h_1.balance, symbol)}</td><td className="py-2 text-right text-gray-400 tabular-nums whitespace-nowrap hidden sm:table-cell">{(h_1.share * 100).toFixed(2)}%</td></tr>;
      $[114] = symbol;
      $[115] = t57;
    } else {
      t57 = $[115];
    }
    t56 = t55.slice(0, 20).map(t57);
    $[111] = symbol;
    $[112] = t55;
    $[113] = t56;
  } else {
    t56 = $[113];
  }
  let t57;
  if ($[116] !== t56) {
    t57 = <div className="overflow-x-auto"><table className="w-full text-xs sm:text-sm">{t54}<tbody>{t56}</tbody></table></div>;
    $[116] = t56;
    $[117] = t57;
  } else {
    t57 = $[117];
  }
  let t58;
  if ($[118] !== t53 || $[119] !== t57) {
    t58 = <div>{t53}{t57}</div>;
    $[118] = t53;
    $[119] = t57;
    $[120] = t58;
  } else {
    t58 = $[120];
  }
  let t59;
  if ($[121] !== t10 || $[122] !== t26 || $[123] !== t46 || $[124] !== t48 || $[125] !== t58) {
    t59 = <section className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(230,15%,8%)] p-4 sm:p-6 space-y-6">{t10}{t26}{t46}{t48}{t58}</section>;
    $[121] = t10;
    $[122] = t26;
    $[123] = t46;
    $[124] = t48;
    $[125] = t58;
    $[126] = t59;
  } else {
    t59 = $[126];
  }
  return t59;
}
function _temp4(label) {
  return formatDate(new Date(label as number));
}
function _temp3(v_0) {
  return v_0 >= 1000 ? `${(v_0 / 1000).toFixed(0)}k` : v_0;
}
function _temp2(v) {
  return formatDate(new Date(v));
}
function _temp(h) {
  return {
    timestamp: new Date(h.date).getTime(),
    holders: h.totalHolders,
    whales: h.whaleCount,
    whaleShare: h.whaleShare * 100,
    top10Share: h.top10Share * 100
  };
}